// Match a cited snippet to the lines of a page at LINE granularity (MDRS-134).
//
// Used against OCR lines (scanned PDFs have no browser text layer). The backend
// `snippet` is the cited passage, but the AI answer that surfaces it is often
// heavily paraphrased and the OCR of a scanned form drifts too (dates reflowed,
// "R) knee" vs "right knee", broken across boxes). So the snippet and the page
// rarely share a long CONTIGUOUS run of words — an earlier longest-common-run
// approach either missed the real passage or latched onto a clean run of
// generic form boilerplate ("Certificate of Capacity ...").
//
// Instead we score each OCR line by how many of its CONTENT words (non-stop,
// deduplicated) also appear anywhere in the snippet — a bag-of-words overlap
// that tolerates paraphrase and reordering. Lines with a strong overlap are
// highlighted; boilerplate that shares nothing with the cited passage scores
// zero and is left alone. When no line clears the bar the caller silently falls
// back to a plain page jump.

// A line is highlighted if it shares at least this many distinct content words
// with the snippet AND those shared words cover at least MIN_MULTI_HIT_COVERAGE
// of the line's own content words...
const MIN_SHARED_CONTENT_WORDS = 2;
// ...the coverage floor for the multi-hit case. Without it a long boilerplate
// line that happens to share two incidental words with the snippet (e.g. a
// generic form line sharing "knee" and "work" with a knee-injury citation) lit
// up even though it is not the cited passage (MDRS-134). Requiring the shared
// words to cover a third of the line keeps genuine multi-line passages while
// dropping incidental long-line overlaps.
const MIN_MULTI_HIT_COVERAGE = 0.34;
// ...OR it shares at least one AND that covers this fraction of its own content
// words (so a short, fully-matched line like "Anxiety and Depression" still
// lights up without needing two hits).
const MIN_LINE_COVERAGE = 0.5;

// High-frequency words that carry no locating signal, plus common medical-form
// scaffolding. A shared word from this set is not evidence a line is the cited
// passage.
const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "been",
  "but",
  "by",
  "can",
  "for",
  "from",
  "had",
  "has",
  "have",
  "he",
  "her",
  "him",
  "his",
  "if",
  "in",
  "into",
  "is",
  "it",
  "its",
  "must",
  "of",
  "on",
  "or",
  "provided",
  "she",
  "that",
  "the",
  "their",
  "this",
  "to",
  "use",
  "used",
  "was",
  "were",
  "which",
  "who",
  "will",
  "with",
  "you",
  "your",
]);

const tokenize = (value: string): string[] =>
  value
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter(Boolean);

const contentWords = (value: string): string[] =>
  tokenize(value).filter((w) => !STOP_WORDS.has(w));

/**
 * Find the line indices a cited snippet touches on a page.
 *
 * Scores each line by its distinct content-word overlap with the snippet and
 * returns the sorted indices of every line that clears the bar, or `null` when
 * nothing does (empty snippet, no lines, or only incidental/stop-word overlap).
 */
export const matchSnippetToLines = (
  lines: string[],
  snippet: string
): number[] | null => {
  const needle = new Set(contentWords(snippet));
  if (needle.size === 0) return null;
  if (lines.length === 0) return null;

  const matched: number[] = [];

  lines.forEach((text, lineIndex) => {
    const lineWords = contentWords(text);
    if (lineWords.length === 0) return;

    const distinct = new Set(lineWords);
    let shared = 0;
    for (const word of distinct) {
      if (needle.has(word)) shared++;
    }
    if (shared === 0) return;

    const coverage = shared / distinct.size;
    const multiHit =
      shared >= MIN_SHARED_CONTENT_WORDS && coverage >= MIN_MULTI_HIT_COVERAGE;
    if (multiHit || coverage >= MIN_LINE_COVERAGE) {
      matched.push(lineIndex);
    }
  });

  return matched.length > 0 ? matched : null;
};
